/**
 * Copyright by INSIDE M2M GmbH and the authors 2004 - 2018
 *
 *************************************************************
 ********************* CONFIDENTIAL **************************
 *************************************************************
 *
 */

import { Injectable, NgZone } from '@angular/core';
import * as alertify from 'alertify.js';

const LS_BUFFER_KEY = 'AlertifyService_errorBuffer';

export interface AlertResult {
    buttonClicked: string;
    inputValue?: string;
    event: Event;
}

@Injectable()
export class AlertifyService {
    public static SUCCESS = 'SUCCESS';
    public static ERROR = 'ERROR';
    public static LOG = 'LOG';

    public errorBufferMaxSize = 20;

    private position: string = 'top right';
    private _errorBuffer: any[] = [];

    constructor(private ngZone: NgZone) {
        const storedBuffer = localStorage.getItem(LS_BUFFER_KEY);
        if (storedBuffer) {
            this._errorBuffer.push(...JSON.parse(storedBuffer));
        }
    }

    alert(message: string) {
        alertify.alert(message);
    }

    confirm(message: string): Promise<AlertResult> {
        return alertify.confirm(message);
    }

    setMaxNumberOfLogs(count: number) {
        alertify.maxLogItems(count);
    }

    setDefaultPosition(position: string) {
        this.position = position;
    }

    appendError(message: string) {
        if (this._errorBuffer.length >= this.errorBufferMaxSize) {
            this._errorBuffer.splice(0, 1);
        }

        this._errorBuffer.push({
            timestamp: new Date(),
            message,
        });

        localStorage.setItem(LS_BUFFER_KEY, JSON.stringify(this._errorBuffer));
    }

    get errorBuffer() {
        return this._errorBuffer;
    }

    clearErrorBuffer() {
        this._errorBuffer.splice(0, this.errorBuffer.length);
        localStorage.removeItem(LS_BUFFER_KEY);
    }

    log(message: string, type: string, position?: string, callback?: (event: Event) => void) {
        this.ngZone.runOutsideAngular(() => {
            alertify.logPosition(position || this.position);
            switch (type) {
                case AlertifyService.SUCCESS: {
                    alertify.success(message, callback);
                    break;
                }
                case AlertifyService.ERROR: {
                    alertify.error(message, callback);
                    this.appendError(message);
                    break;
                }
                case AlertifyService.LOG: {
                    alertify.log(message, callback);
                    break;
                }
                default: {
                    break;
                }
            }
        });
    }
}
